import { useState, type FormEvent, type ReactNode } from 'react';
import { CheckCircle2, Eye, EyeOff } from 'lucide-react';
import { Button, Input, Typography, type InputProps } from '@inference/ui';
import authMeshFlow from '@/assets/auth-mesh-flow.jpg';
import navaCompanyMark from '@/assets/nava-company-mark.svg';
import type { PasswordRule } from '@/lib/password-rules';

export type AuthShellProps = {
  title: string;
  subtitle?: string;
  children: ReactNode;
};

/** Figma 2799:49775 left column — fluid 1024–1600, hidden below lg. */
function AuthMarketingPanel() {
  return (
    <aside
      className="hidden min-w-0 flex-1 basis-0 flex-col pb-space-40 pt-[clamp(4rem,calc(2rem+6.5vw),12.5rem)] px-[clamp(1.5rem,calc(1rem+4vw),7.5rem)] lg:flex"
      aria-hidden="true"
    >
      <div className="mx-auto flex min-h-0 w-full max-w-[40rem] flex-1 flex-col justify-between">
        <div className="flex flex-col gap-[clamp(1.5rem,calc(1rem+2vw),4rem)]">
          <img
            src={navaCompanyMark}
            alt=""
            className="size-[clamp(3rem,calc(2rem+2.5vw),5rem)] shrink-0"
            width={80}
            height={80}
          />
          <div className="flex w-full flex-col gap-space-16">
            <Typography
              variant="header-xl"
              weight="semibold"
              className="tracking-[-0.02em] ![font-size:clamp(1.375rem,calc(1rem+1.25vw),2rem)] ![line-height:clamp(1.75rem,calc(1.25rem+1.5vw),2.75rem)]"
            >
              Infrastructure for Inference, simplified.
            </Typography>
            <Typography
              variant="text-xl"
              className="text-tertiary ![font-size:clamp(0.9375rem,calc(0.75rem+0.4vw),1.25rem)] ![line-height:clamp(1.375rem,calc(1rem+0.6vw),1.875rem)]"
            >
              Find the right model, hardware, and deployment setup for your AI workload.
            </Typography>
          </div>
        </div>
        <Typography variant="text-md" className="font-mono uppercase text-tertiary">
          © 2026 NAVA. All Rights Reserved.
        </Typography>
      </div>
    </aside>
  );
}

/**
 * Figma 2799:49775 — marketing left, mesh + form right.
 * <lg: form only, centered. lg+: split; card height follows content.
 */
export function AuthShell({ title, subtitle, children }: AuthShellProps) {
  return (
    <main className="flex min-h-screen bg-background">
      <div className="mx-auto flex min-h-screen w-full  items-stretch overflow-x-hidden">
        <AuthMarketingPanel />
        <section className="relative flex min-w-0 flex-1 basis-0 flex-col items-center justify-center px-space-24 py-space-40 lg:px-[clamp(1.5rem,calc(1rem+4vw),7.5rem)]">
          <div
            className="pointer-events-none absolute inset-0 overflow-hidden opacity-50"
            aria-hidden="true"
          >
            <img
              src={authMeshFlow}
              alt=""
              className="absolute left-[-57%] top-[-10%] h-[119%] w-[214%] max-w-none object-cover"
            />
          </div>
          <div className="relative flex min-h-0 w-full min-w-0 max-w-[33.75rem] flex-col gap-space-24 rounded-[var(--radius-24)] bg-elevated p-space-40 shadow-lg">
            <img
              src={navaCompanyMark}
              alt=""
              className="mb-space-16 size-12 shrink-0 self-start lg:hidden"
              width={48}
              height={48}
            />
            <div className="flex flex-col gap-space-8">
              <Typography
                variant="header-md"
                weight="semibold"
                as="h1"
                className="tracking-[-0.02em]"
              >
                {title}
              </Typography>
              {subtitle ? (
                <Typography variant="text-md" className="text-tertiary">
                  {subtitle}
                </Typography>
              ) : null}
            </div>
            {children}
          </div>
        </section>
      </div>
    </main>
  );
}

export type FieldProps = Omit<InputProps, 'onChange' | 'size' | 'type'> & {
  type?: 'text' | 'email' | 'password';
  size?: InputProps['size'];
  onChange?: (value: string) => void;
};

export function Field({
  type = 'text',
  size = 'xl',
  required = true,
  onChange,
  ...rest
}: FieldProps) {
  return (
    <Input
      type={type}
      size={size}
      required={required}
      onChange={(event) => onChange?.(event.target.value)}
      {...rest}
    />
  );
}

export type PasswordFieldProps = Omit<InputProps, 'onChange' | 'size' | 'type' | 'trailing'> & {
  size?: InputProps['size'];
  onChange: (value: string) => void;
};

export function PasswordField({
  size = 'xl',
  autoComplete = 'current-password',
  required = true,
  onChange,
  ...rest
}: PasswordFieldProps) {
  const [revealed, setRevealed] = useState(false);

  return (
    <Input
      {...rest}
      size={size}
      type={revealed ? 'text' : 'password'}
      autoComplete={autoComplete}
      required={required}
      onChange={(event) => onChange(event.target.value)}
      trailing={
        <button
          type="button"
          className="cursor-pointer text-tertiary hover:text-foreground"
          aria-label={revealed ? 'Hide password' : 'Show password'}
          aria-controls={rest.id}
          onClick={() => setRevealed((current) => !current)}
        >
          {revealed ? <EyeOff className="size-icon-md" /> : <Eye className="size-icon-md" />}
        </button>
      }
    />
  );
}

export type AuthFormProps = {
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  children: ReactNode;
  submitLabel: string;
  submitting?: boolean;
  disabled?: boolean;
  error?: string | null;
};

export function AuthForm({
  onSubmit,
  children,
  submitLabel,
  submitting,
  disabled,
  error,
}: AuthFormProps) {
  return (
    <form className="flex w-full flex-col gap-space-20" onSubmit={onSubmit}>
      {error ? (
        <Typography
          variant="text-sm"
          className="rounded-8 border border-border-danger bg-danger-subtle p-space-12 text-error-solid"
        >
          {error}
        </Typography>
      ) : null}
      {children}
      <Button
        type="submit"
        variant="primary"
        size="xl"
        block
        loading={submitting}
        disabled={disabled}
        className="h-10"
      >
        {submitLabel}
      </Button>
    </form>
  );
}

/** Auth0 screen.loginLink / signupLink — keeps invitation ticket when switching. */
export function AuthSwitchLink({
  href,
  prompt,
  action,
}: {
  href: string | null;
  prompt: string;
  action: string;
}) {
  if (!href) {
    return null;
  }
  return (
    <Typography variant="text-sm" className="text-tertiary">
      {prompt}{' '}
      <a
        href={href}
        className="cursor-pointer font-medium text-brand underline-offset-2 hover:underline"
      >
        {action}
      </a>
    </Typography>
  );
}

export type CaptchaScreenBits = {
  isCaptchaAvailable: boolean;
  captchaImage: string | null;
  captchaProvider: string | null;
};

/** Auth0 image captcha when present. Other providers need a widget — see README. */
export function CaptchaField({
  screen,
  value,
  onChange,
}: {
  screen: CaptchaScreenBits;
  value: string;
  onChange: (value: string) => void;
}) {
  if (!screen.isCaptchaAvailable) {
    return null;
  }

  if (!screen.captchaImage) {
    return (
      <Typography variant="text-sm" className="text-error-solid">
        CAPTCHA is required ({screen.captchaProvider ?? 'unknown'}). This screen only supports Auth0
        image captcha — disable bot detection or use the Auth0 provider.
      </Typography>
    );
  }

  return (
    <div className="flex flex-col gap-space-12">
      <img
        className="h-auto max-w-full rounded-8 border border-border"
        src={screen.captchaImage}
        alt=""
      />
      <Field
        id="captcha"
        label="Enter the code shown above"
        value={value}
        autoComplete="off"
        onChange={onChange}
      />
    </div>
  );
}

export function captchaPayload(screen: CaptchaScreenBits, captcha: string): { captcha?: string } {
  if (!screen.isCaptchaAvailable) {
    return {};
  }
  return { captcha };
}

export function isCaptchaReady(screen: CaptchaScreenBits, captcha: string): boolean {
  if (!screen.isCaptchaAvailable) {
    return true;
  }
  if (!screen.captchaImage) {
    return false;
  }
  return captcha.trim().length > 0;
}

export function PasswordRulesList({ rules }: { rules: PasswordRule[] }) {
  return (
    <ul className="flex flex-col gap-space-6">
      {rules.map((rule) => {
        let iconClass = 'size-icon-xs shrink-0 text-tertiary';
        if (rule.ok) {
          iconClass = 'size-icon-xs shrink-0 text-brand';
        }
        return (
          <li key={rule.id} className="flex items-center gap-space-4">
            <CheckCircle2 className={iconClass} aria-hidden />
            <Typography variant="text-xs" weight="regular" className="text-tertiary">
              {rule.label}
            </Typography>
          </li>
        );
      })}
    </ul>
  );
}
